import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Input from '../../components/ui/Input';
import DatePicker from '../../components/ui/DatePicker';
import Badge from '../../components/ui/Badge';
import EmptyState from '../../components/ui/EmptyState';
import Spinner from '../../components/ui/Spinner';
import { listActivityLogs } from '../../api/activityLogs';

export default function ActivityLog() {
  const [actionInput, setActionInput] = useState('');
  const [action, setAction] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setAction(actionInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [actionInput]);

  const params = { action: action || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined, page };
  const logsQuery = useQuery({ queryKey: ['activity-logs', params], queryFn: () => listActivityLogs(params) });

  return (
    <div>
      <PageHeader title="Activity Log" />

      <div className="mb-4 flex flex-wrap gap-3">
        <Input
          placeholder="Filter by action, e.g. candidate.created"
          value={actionInput}
          onChange={(e) => setActionInput(e.target.value)}
          className="w-72"
        />
        <DatePicker value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="w-40" />
        <DatePicker value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="w-40" />
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        {logsQuery.isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : (logsQuery.data?.data || []).length === 0 ? (
          <EmptyState title="No activity found." />
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Action</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Description</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Candidate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {logsQuery.data.data.map((log) => (
                <Fragment key={log.id}>
                  <tr
                    onClick={() => log.changes && setExpanded(expanded === log.id ? null : log.id)}
                    className={`hover:bg-gray-50 ${log.changes ? 'cursor-pointer' : ''}`}
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-gray-500">{new Date(log.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3 text-gray-700">{log.user || '—'}</td>
                    <td className="px-4 py-3">
                      <Badge>{log.action}</Badge>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{log.description}</td>
                    <td className="px-4 py-3">
                      {log.candidate_id ? (
                        <Link
                          to={`/candidates/${log.candidate_id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary hover:underline"
                        >
                          {log.candidate}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                  {expanded === log.id && log.changes && (
                    <tr>
                      <td colSpan={5} className="bg-gray-50 px-4 py-3">
                        <pre className="overflow-x-auto text-xs text-gray-700">{JSON.stringify(log.changes, null, 2)}</pre>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}

        {logsQuery.data && logsQuery.data.last_page > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-sm text-gray-500">
            <span>
              Page {logsQuery.data.current_page} of {logsQuery.data.last_page}
            </span>
            <div className="flex gap-2">
              <button
                disabled={logsQuery.data.current_page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="rounded-md border border-gray-300 px-3 py-1 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                disabled={logsQuery.data.current_page >= logsQuery.data.last_page}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-md border border-gray-300 px-3 py-1 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
